import { observer } from 'mobx-react-lite';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { EventPanelTab, EventPanelTabConfig } from './event-panel-tabs-config';
import { TabList, Tabs } from '@cad-arc';
import { TabHeader } from './tab-header';
import { getEventPanelTabTooltip } from '../../../common/helpers/get-usx-labels';
import { useIsDexEnabled } from '../../../data-exchange/use-dex-access-guard';
import { useFederatedSearchRemoteState } from '../../../federated-search/federated-search-remote-state-context';
import styles from './event-panel-tabs.module.css';

/**
 * The panel was originally built for 12 tabs, and every conditional tab enabled adds one: up to 14
 * with Sensor Events. The row is fixed-width and hides what overflows, so tabs narrow as the count
 * grows. 13 tabs at 2.4rem fill it, so 14 get 2.2rem (with tighter label padding, `compactTabs`,
 * so each tab still fits its slot).
 */
function getTabHeaderWidth(tabCount: number): string {
    if (tabCount > 13) {
        return '2.2rem';
    }
    return tabCount > 12 ? '2.4rem' : 'max-content';
}

export const TabHeaders = observer(function TabHeaders({
    activeTab,
    tabConfigs,
}: {
    activeTab?: EventPanelTab;
    tabConfigs: EventPanelTabConfig[];
}) {
    const eventMetadata = useEventMetadataStore().state.metadata;
    const enabledFeatures = useEnabledFeatures();
    const { isDexEnabled } = useIsDexEnabled();
    const federatedSearchRemoteState = useFederatedSearchRemoteState();
    const isDexTabAvailable =
        isDexEnabled &&
        (!enabledFeatures.dexNextEnabled || federatedSearchRemoteState === 'available');
    const tabsToShow = tabConfigs.filter((tabConfig) => {
        if (tabConfig.type === 'DEX' && !isDexTabAvailable) return false;
        if (typeof tabConfig.checkEnabled === 'undefined') return true;
        return tabConfig.checkEnabled(enabledFeatures, eventMetadata);
    });

    const tabHeaderWidth = getTabHeaderWidth(tabsToShow.length);

    return (
        <Tabs
            loop={false}
            isManual={true}
            value={activeTab}
            style={{
                width: '100%',
                backgroundColor: 'var(--arc-colors-surface-foreground)',
                zIndex: 5,
            }}
        >
            <TabList
                className={tabsToShow.length > 13 ? styles.compactTabs : undefined}
                style={{
                    borderBottom: 'none',
                    borderTop: 'solid 1px var(--arc-colors-border-default)',
                    width: '100%',
                    overflowX: 'hidden',
                }}
            >
                {tabsToShow.map((tabConfig) => (
                    <TabHeader
                        key={tabConfig.type}
                        value={tabConfig.type}
                        leadingVisual={tabConfig.icon}
                        tooltip={getEventPanelTabTooltip(
                            tabConfig.name,
                            enabledFeatures.dexNextEnabled
                        )}
                        errorFields={tabConfig.errorFields}
                        notificationTypes={
                            'notificationTypes' in tabConfig
                                ? tabConfig.notificationTypes
                                : undefined
                        }
                        tabHeaderWidth={tabHeaderWidth}
                        notificationsEntityTypes={
                            'notificationsEntityTypes' in tabConfig
                                ? tabConfig.notificationsEntityTypes
                                : undefined
                        }
                    />
                ))}
            </TabList>
        </Tabs>
    );
});
